
import ReactDOM from 'react-dom/client';
import { StrictMode } from 'react'
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from './components/ErrorBoundary';
import { loadGTM } from './helpers/loadGTM';
import { reloadForStaleChunks } from './helpers/lazyWithRetry';

// Prevent unhandled promise rejections (from blocked trackers) from crashing the page
window.addEventListener('unhandledrejection', (event) => {
    event.preventDefault();
});

// Vite fires this when a <link rel="modulepreload"> for a code-split chunk fails — the
// stale-hashes-after-a-deploy case (see src/helpers/lazyWithRetry.js), caught here before
// the route's import() gets a chance to reject. Reloading picks up the current build.
window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadForStaleChunks();
});

// Inject GTM (and its child tags) after load + idle to keep them off the
// critical/TBT path. No-ops outside production. See src/helpers/loadGTM.js.
loadGTM();

const AppTree = () => (
    <StrictMode>
    


        <HelmetProvider>
            <BrowserRouter>
                {/* Inside BrowserRouter so the ErrorModal fallback (useNavigate/Link)
                    has Router context, while still wrapping all of App. */}
                <ErrorBoundary>
                    <div className='min-vh-100 d-flex flex-column'>
                        <App />
                    </div>
                </ErrorBoundary>
            </BrowserRouter>
        </HelmetProvider>
    </StrictMode>
);

// index.html hardcodes a static SEO "floor" (title/description/og:*/twitter:*/canonical/
// hreflang) for crawlers that never execute JS. react-helmet-async only ever manages
// elements it already tagged with data-rh="true" on a previous render (see updateTags in
// react-helmet-async/lib/index.js) — it never removes plain tags that were already in the
// DOM before React mounted. Left alone, every <SeoHelmet> render on every route ends up
// appending a second, correct set of tags AFTER these static ones instead of replacing
// them, so anything reading the first matching tag (most OG/Twitter parsers do) sees the
// stale static values. Strip the floor tags here, synchronously before the first render,
// so Helmet's own tags become the only set once React takes over.
const SEO_FLOOR_SELECTOR = [
    'meta[name="description"]',
    'meta[property^="og:"]',
    'meta[name^="twitter:"]',
    'link[rel="canonical"]',
    'link[rel="alternate"][hreflang]',
].join(', ');
document.head.querySelectorAll(SEO_FLOOR_SELECTOR).forEach((el) => {
    if (!el.hasAttribute('data-rh')) el.remove();
});

const container = document.getElementById('root');
document.documentElement.classList.add('anim-ready');

// lambda/ssr-handler.js server-renders real markup into #root for the SSR-eligible routes
// (see SSR-MIGRATION-PLAN.md / src/entry-server.js) — hydrate that instead of replacing it,
// or React would discard the server HTML and pay for a second full client render. Every
// other route still gets the empty S3 shell (#root has no children), so createRoot is
// correct there exactly as before.
if (container.hasChildNodes()) {
    ReactDOM.hydrateRoot(container, <AppTree />);
} else {
    ReactDOM.createRoot(container).render(<AppTree />);
}

if (import.meta.hot) {
    import.meta.hot.accept();
}


// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
